import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import {
    Briefcase,
    Users,
    Calendar,
    TrendingUp,
    TrendingDown,
    CheckCircle,
    MoreHorizontal,
    ArrowRight,
    X,
    Wrench
} from 'lucide-react';
import { companyApiService } from '../services/companyApi';
import { RecentJob } from '../types';

/**
 * Company Dashboard Page
 * Comprehensive dashboard with statistics, schedules, and job management
 */
const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [companyData, setCompanyData] = useState({
        name: 'Company',
        email: '',
        activeJobs: 0, // Fetched dynamically from API
        profileStrength: 0, // Fetched from companyData in localStorage
        candidatesToReview: 0, // Fetched dynamically from applications API

        interviews: 0, // Fetched dynamically - applications in 'interview' stage
        newApplicants: 0, // Fetched dynamically - applications in 'applied' stage
        interviewPipeline: 0, // Fetched dynamically - all non-final stages
    });
    const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
    const [jobsLoading, setJobsLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [selectedView, setSelectedView] = useState<'Week' | 'Month' | 'Year'>('Week');
    const [showScheduleModal, setShowScheduleModal] = useState(false);

    // Compute dynamic calendar days (yesterday, today, tomorrow, day after)
    const calendarDays = React.useMemo(() => {
        const today = new Date();
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return [-1, 0, 1, 2].map(offset => {
            const d = new Date(today);
            d.setDate(today.getDate() + offset);
            return {
                dayName: dayNames[d.getDay()],
                date: d.getDate(),
                isToday: offset === 0,
            };
        });
    }, []);

    useEffect(() => {
        // Check authentication
        const token = localStorage.getItem('authToken');
        const user = localStorage.getItem('authUser');

        if (!token || !user) {
            navigate('/auth/login', { replace: true });
            return;
        }

        // Fetch company data
        fetchCompanyData();
    }, [navigate]);

    const fetchCompanyData = async () => {
        try {
            setLoading(true);
            const userDataString = localStorage.getItem('authUser');

            if (userDataString) {
                const userData = JSON.parse(userDataString);

                // Fetch company data from localStorage
                const companyDataString = localStorage.getItem('companyData');
                if (companyDataString) {
                    const storedCompanyData = JSON.parse(companyDataString);
                    setCompanyData(prev => ({
                        ...prev,
                        name: storedCompanyData.name || storedCompanyData.company_name || userData.first_name || 'Company',
                        email: userData.email || '',
                        profileStrength: storedCompanyData.profileStrength || 0,
                    }));
                } else {
                    setCompanyData(prev => ({
                        ...prev,
                        name: userData.company_name || userData.companyName || userData.first_name || userData.name || 'Company',
                        email: userData.email || '',
                    }));
                }
            }

            // Fetch recent jobs, active jobs count, and candidates to review from API
            await Promise.all([
                fetchRecentJobs(),
                fetchActiveJobsCount(),
                fetchCandidatesToReview()
            ]);
        } catch (error) {
            console.error('Failed to fetch company data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRecentJobs = async () => {
        try {
            setJobsLoading(true);
            const jobs = await companyApiService.getRecentJobs(5);
            console.log('Fetched recent jobs:', jobs);
            setRecentJobs(jobs);
        } catch (error) {
            console.error('Failed to fetch recent jobs:', error);
            setRecentJobs([]);
        } finally {
            setJobsLoading(false);
        }
    };

    const fetchActiveJobsCount = async () => {
        try {
            const { total, active } = await companyApiService.getActiveJobsCount();
            console.log('Fetched active jobs count:', { total, active });
            setCompanyData(prev => ({
                ...prev,
                activeJobs: active,
            }));
        } catch (error) {
            console.error('Failed to fetch active jobs count:', error);
        }
    };

    const fetchCandidatesToReview = async () => {
        try {
            const { total, byStage } = await companyApiService.getCandidatesToReview();
            console.log('Fetched candidates to review:', { total, byStage });

            // Calculate stats from byStage breakdown
            const appliedCount = byStage['applied'] || 0;
            const interviewCount = byStage['interview'] || 0;
            // Pipeline = all stages except final ones (offered, rejected, withdrawn)
            const pipelineCount = (byStage['applied'] || 0) +
                                  (byStage['interview'] || 0);

            setCompanyData(prev => ({
                ...prev,
                candidatesToReview: total,
                newApplicants: appliedCount,
                interviews: interviewCount,
                interviewPipeline: pipelineCount,
            }));
        } catch (error) {
            console.error('Failed to fetch candidates to review:', error);
        }
    };

    if (loading) {
        return (
            <CompanyLayout>
                <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6] mb-4"></div>
                        <p className="text-base font-medium text-gray-500">Loading dashboard...</p>
                    </div>
                </div>
            </CompanyLayout>
        );
    }

    const todoItems = [
        { date: '13 October, 2025', task: 'Add company description', status: 'Completed' },
        { date: '13 October, 2025', task: 'Update salary range for UX Designer Job', status: 'Upcoming' },
        { date: '14 February, 2024', task: 'Verify company email', status: 'Completed' },
    ];


    return (
        <CompanyLayout>
            <div className="space-y-6">
                {/* Top Stats Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                    {/* Active Job Posts */}
                    <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 group">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Briefcase className="w-6 h-6 text-[#015FA6]" />
                            </div>
                            <div>
                                <div className="text-sm text-gray-500 font-medium">Active Job</div>
                                <div className="text-sm text-gray-500 font-medium">Posts</div>
                            </div>
                        </div>
                        <div className="text-3xl font-bold text-[#0B111E] mb-2">{companyData.activeJobs}</div>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="px-2.5 py-1 bg-blue-50 text-[#015FA6] text-xs rounded-lg font-semibold">Complete</span>
                        </div>
                        <div className="h-12 w-full overflow-hidden rounded-lg">
                            <svg width="100%" height="100%" viewBox="0 0 200 64" preserveAspectRatio="none" className="text-blue-500/20">
                                <path d="M 0 48 Q 50 32 100 40 T 200 24 L 200 64 L 0 64 Z" fill="currentColor" />
                                <path d="M 0 48 Q 50 32 100 40 T 200 24" stroke="#3B82F6" strokeWidth="2" fill="none" />
                            </svg>
                        </div>
                    </div>

                    {/* Profile Strength */}
                    <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 group">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-yellow-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <div className="grid grid-cols-2 gap-1">
                                    <div className="w-2 h-2 bg-yellow-500 rounded-sm"></div>
                                    <div className="w-2 h-2 bg-yellow-500 rounded-sm"></div>
                                    <div className="w-2 h-2 bg-yellow-500 rounded-sm"></div>
                                    <div className="w-2 h-2 bg-yellow-500 rounded-sm"></div>
                                </div>
                            </div>
                            <div>
                                <div className="text-sm text-gray-500 font-medium">Profile</div>
                                <div className="text-sm text-gray-500 font-medium">Strength</div>
                            </div>
                        </div>
                        <div className="text-3xl font-bold text-[#0B111E] mb-2">{companyData.profileStrength}%</div>
                        <div className="flex items-center gap-2 mb-4">
                            <span className={`px-2.5 py-1 text-xs rounded-lg font-semibold ${
                                companyData.profileStrength >= 80 ? 'bg-green-50 text-green-600' :
                                companyData.profileStrength >= 50 ? 'bg-yellow-50 text-yellow-600' :
                                'bg-red-50 text-red-600'
                            }`}>
                                {companyData.profileStrength >= 80 ? 'Complete' :
                                 companyData.profileStrength >= 50 ? 'Good' : 'Needs Work'}
                            </span>
                        </div>
                        <div className="h-12 w-full overflow-hidden rounded-lg">
                            <svg width="100%" height="100%" viewBox="0 0 200 64" preserveAspectRatio="none" className="text-yellow-500/20">
                                <path d="M 0 40 Q 50 28 100 36 T 200 32 L 200 64 L 0 64 Z" fill="currentColor" />
                                <path d="M 0 40 Q 50 28 100 36 T 200 32" stroke="#F59E0B" strokeWidth="2" fill="none" />
                            </svg>
                        </div>
                    </div>

                    {/* Candidates to Review */}
                    <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 group flex flex-col justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Users className="w-7 h-7 text-purple-600" />
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-[#0B111E]">{companyData.candidatesToReview}</div>
                                <div className="text-sm text-gray-500 font-medium">Candidates to review</div>
                            </div>
                        </div>
                        <button onClick={() => navigate('/companies/applications-review')} className="mt-4 w-full flex items-center justify-center gap-2 text-[#015FA6] hover:text-[#014a85] transition-colors py-2 rounded-xl hover:bg-blue-50/50">
                            <span className="text-sm font-semibold">View Details</span>
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Interviews */}
                    <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 group flex flex-col justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Calendar className="w-7 h-7 text-orange-500" />
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-[#0B111E]">{companyData.interviews}</div>
                                <div className="text-sm text-gray-500 font-medium">Interviews</div>
                            </div>
                        </div>
                        <button onClick={() => navigate('/companies/applications-review')} className="mt-4 w-full flex items-center justify-center gap-2 text-[#015FA6] hover:text-[#014a85] transition-colors py-2 rounded-xl hover:bg-blue-50/50">
                            <span className="text-sm font-semibold">View Details</span>
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Stats and Todo */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* New Applicants and Interview Pipeline */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* New Applicants */}
                            <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center">
                                        <Users className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500 font-medium">New Applicants</div>
                                        <div className="text-sm text-gray-500">This Week</div>
                                    </div>
                                </div>
                                <div className="flex items-end justify-between mb-4">
                                    <div className="text-4xl font-bold text-[#0B111E]">{companyData.newApplicants}</div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-500">Ongoing</span>
                                        <span className="px-2.5 py-1 bg-green-50 text-green-600 text-xs rounded-lg font-semibold">Great</span>
                                    </div>
                                </div>
                                <div className="h-20 w-full overflow-hidden rounded-xl">
                                    <svg width="100%" height="100%" viewBox="0 0 300 80" preserveAspectRatio="none" className="text-green-500/20">
                                        <path d="M 0 60 Q 75 40 150 50 T 300 30 L 300 80 L 0 80 Z" fill="currentColor" />
                                        <path d="M 0 60 Q 75 40 150 50 T 300 30" stroke="#10B981" strokeWidth="2" fill="none" />
                                    </svg>
                                </div>
                            </div>

                            {/* Interview Pipeline */}
                            <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-orange-500 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500 font-medium">Interview</div>
                                        <div className="text-sm text-gray-500">Pipeline</div>
                                    </div>
                                </div>
                                <div className="flex items-end justify-between mb-4">
                                    <div className="text-4xl font-bold text-[#0B111E]">{companyData.interviewPipeline}</div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-500">Last 7 Days</span>
                                        <span className="px-2.5 py-1 bg-orange-50 text-orange-600 text-xs rounded-lg font-semibold">Normal</span>
                                    </div>
                                </div>
                                <div className="h-20 w-full overflow-hidden rounded-xl">
                                    <svg width="100%" height="100%" viewBox="0 0 300 80" preserveAspectRatio="none" className="text-orange-500/20">
                                        <path d="M 0 50 Q 75 35 150 45 T 300 40 L 300 80 L 0 80 Z" fill="currentColor" />
                                        <path d="M 0 50 Q 75 35 150 45 T 300 40" stroke="#F97316" strokeWidth="2" fill="none" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Job Statistics */}
                        <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                            <div className="flex flex-col md:flex-row justify-between md:items-center mb-8 gap-4">
                                <div>
                                    <h3 className="text-lg font-bold text-[#0B111E]">Job statistics</h3>
                                    <p className="text-sm text-gray-500 mt-1">Showing Job statistics Id 19-25</p>
                                </div>
                                <div className="flex bg-gray-100/50 p-1 rounded-xl">
                                    {['Week', 'Month', 'Year'].map((view) => (
                                        <button
                                            key={view}
                                            onClick={() => setSelectedView(view as any)}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${selectedView === view
                                                ? 'bg-white text-[#015FA6] shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                        >
                                            {view}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Statistics Summary */}
                            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-blue-500 rounded-full ring-4 ring-blue-100"></div>
                                        <span className="text-sm font-medium text-gray-600">Job views</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-yellow-500 rounded-full ring-4 ring-yellow-100"></div>
                                        <span className="text-sm font-medium text-gray-600">Job Applied</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-orange-500 rounded-full ring-4 ring-orange-100"></div>
                                        <span className="text-sm font-medium text-gray-600">Job Opened</span>
                                    </div>
                                </div>
                            </div>

                            {/* Chart */}
                            <div className="h-64 relative w-full">
                                <svg width="100%" height="100%" viewBox="0 0 800 250" preserveAspectRatio="none">
                                    {/* Grid lines */}
                                    {[50, 100, 150, 200].map((y, i) => (
                                        <line key={i} x1="0" y1={y} x2="800" y2={y} stroke="#E5E7EB" strokeWidth="1" strokeDasharray="4 4" />
                                    ))}

                                    {/* Job views line */}
                                    <path d="M 0 180 L 114 160 L 228 140 L 342 130 L 456 120 L 570 100 L 684 80 L 800 70" stroke="#3B82F6" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                    {/* Job Applied line */}
                                    <path d="M 0 200 L 114 185 L 228 170 L 342 160 L 456 145 L 570 130 L 684 110 L 800 95" stroke="#EAB308" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                    {/* Job Opened line */}
                                    <path d="M 0 210 L 114 195 L 228 185 L 342 175 L 456 165 L 570 150 L 684 135 L 800 120" stroke="#F97316" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>

                                {/* X-axis labels */}
                                <div className="absolute bottom-0 left-0 right-0 flex justify-between px-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    <span>Sun</span>
                                    <span>Mon</span>
                                    <span>Tue</span>
                                    <span>Wed</span>
                                    <span>Thu</span>
                                    <span>Fri</span>
                                    <span>Sat</span>
                                </div>
                            </div>

                            {/* Additional metrics */}
                            <div className="flex items-center justify-end gap-8 mt-6 pt-6 border-t border-gray-100">
                                <div className="text-right">
                                    <div className="flex items-center justify-end gap-2 mb-1">
                                        <TrendingDown className="w-4 h-4 text-red-500" />
                                        <span className="text-sm text-gray-500">Job applied</span>
                                        <span className="text-lg font-bold text-[#0B111E]">34</span>
                                    </div>
                                    <div className="text-xs text-gray-400">This week <span className="text-red-500 font-medium">↓5.4%</span></div>
                                </div>
                                <div className="text-right">
                                    <div className="flex items-center justify-end gap-2 mb-1">
                                        <TrendingUp className="w-4 h-4 text-green-500" />
                                        <span className="text-sm text-gray-500">Job opened</span>
                                        <span className="text-lg font-bold text-[#0B111E]">34</span>
                                    </div>
                                    <div className="text-xs text-gray-400">This week <span className="text-green-500 font-medium">↑8.4%</span></div>
                                </div>
                            </div>
                        </div>

                        {/* To Do List */}
                        <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                            <h3 className="text-lg font-bold text-[#0B111E] mb-6">To Do List</h3>
                            <div className="space-y-4">
                                {todoItems.map((item, index) => (
                                    <div key={index} className="flex items-center justify-between p-4 bg-white/50 rounded-2xl border border-white/40 hover:bg-white/80 transition-colors group">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                                                <CheckCircle className="w-5 h-5 text-[#015FA6]" />
                                            </div>
                                            <div>
                                                <div className="text-xs text-[#015FA6] font-semibold mb-0.5">{item.date}</div>
                                                <div className="text-sm text-[#0B111E] font-medium">{item.task}</div>
                                            </div>
                                        </div>
                                        <span className={`px-3 py-1 text-xs font-semibold rounded-lg ${item.status === 'Completed'
                                            ? 'bg-blue-50 text-[#015FA6]'
                                            : 'bg-yellow-50 text-yellow-600'
                                            }`}>
                                            {item.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recently Posted Jobs */}
                        <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-[#0B111E]">Recently Posted Jobs</h3>
                                <button
                                    onClick={() => navigate('/companies/projects')}
                                    className="text-sm font-semibold text-[#015FA6] flex items-center gap-1 hover:underline"
                                >
                                    View All
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                {jobsLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#015FA6]"></div>
                                        <span className="ml-3 text-gray-500">Loading jobs...</span>
                                    </div>
                                ) : recentJobs.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500 font-medium">No jobs posted yet</p>
                                        <p className="text-gray-400 text-sm mt-1">Create your first job posting to get started</p>
                                        <button
                                            onClick={() => navigate('/companies/job-posting-method')}
                                            className="mt-4 px-6 py-2 bg-[#015FA6] text-white rounded-xl text-sm font-semibold hover:bg-[#014a85] transition-colors"
                                        >
                                            Post a Job
                                        </button>
                                    </div>
                                ) : (
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-200/60">
                                                <th className="text-left py-4 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Jobs</th>
                                                <th className="text-center py-4 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                                <th className="text-center py-4 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Applications</th>
                                                <th className="text-center py-4 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Salary</th>
                                                <th className="text-center py-4 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentJobs.map((job) => (
                                                <tr key={job.id} className="border-b border-gray-100 last:border-0 hover:bg-white/50 transition-colors">
                                                    <td className="py-4 px-4">
                                                        <div className="text-sm font-bold text-[#0B111E]">{job.title}</div>
                                                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                                            <span>{job.type}</span>
                                                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                                            <span>{job.daysRemaining} days remaining</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${
                                                            job.status === 'active'
                                                                ? 'bg-green-50 text-green-600'
                                                                : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                            {job.status === 'active' ? 'Active' : 'Closed'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <Users className="w-4 h-4 text-blue-500" />
                                                            <span className="text-sm font-medium text-gray-700">{job.applications} Applications</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {job.salaryMin && job.salaryMax
                                                                    ? `$${job.salaryMin}k - $${job.salaryMax}k`
                                                                    : job.salaryMin
                                                                        ? `$${job.salaryMin}k+`
                                                                        : 'Not specified'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <button
                                                            onClick={() => navigate(`/companies/jobs/${job.id}/applications`)}
                                                            className="px-4 py-2 text-xs font-semibold text-[#015FA6] border border-[#015FA6]/20 bg-[#015FA6]/5 rounded-xl hover:bg-[#015FA6] hover:text-white transition-all duration-200"
                                                        >
                                                            View Applications
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Schedule */}
                    <div className="space-y-6">
                        {/* Schedule Widget */}
                        <div className="bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-bold text-[#0B111E]">Schedule</h3>
                                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <MoreHorizontal className="w-5 h-5 text-gray-400" />
                                </button>
                            </div>

                            {/* Calendar Days */}
                            <div className="grid grid-cols-4 gap-3 mb-8">
                                {calendarDays.map((day, index) => (
                                    <div key={index} className={`text-center ${day.isToday ? '' : 'group'} cursor-pointer`}>
                                        <div className={`text-xs mb-2 font-medium ${day.isToday ? 'text-[#015FA6] font-bold' : 'text-gray-500'}`}>{day.dayName}</div>
                                        <div className={`w-12 h-12 mx-auto flex items-center justify-center rounded-2xl text-sm font-bold transition-colors ${
                                            day.isToday
                                                ? 'bg-[#015FA6] text-white shadow-lg shadow-blue-500/30'
                                                : 'bg-white border border-gray-100 text-gray-900 group-hover:border-[#015FA6]'
                                        }`}>{day.date}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Under Maintenance Notice */}
                            <div className="text-center py-6">
                                <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                    <Wrench className="w-6 h-6 text-orange-500" />
                                </div>
                                <p className="text-sm font-semibold text-[#0B111E] mb-1">Under Maintenance</p>
                                <p className="text-xs text-gray-500">Scheduling is currently being improved. Coming soon.</p>
                            </div>
                        </div>

                        {/* Quick Actions or Promo Card */}
                        <div className="bg-white/60 backdrop-blur-xl border border-[#015FA6]/20 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                            <h3 className="text-lg font-bold mb-2 text-[#0B111E]">Post a New Job</h3>
                            <p className="text-gray-500 text-sm mb-6">Find the perfect candidate for your team today.</p>

                            <button
                                onClick={() => navigate('/companies/job-posting-method')}
                                className="w-full py-3 bg-[#015FA6] text-white font-bold text-sm rounded-xl hover:bg-[#014a85] transition-colors"
                            >
                                Create Job Post
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <footer className="mt-12 py-6 border-t border-gray-200/60">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-gray-500">
                            © 2025 Hush. All rights reserved.
                        </p>
                        <div className="flex items-center gap-6">
                            <a href="/terms-of-service" className="text-sm text-gray-500 hover:text-[#015FA6] transition-colors">
                                Terms of Service
                            </a>
                            <a href="/privacy-policy" className="text-sm text-gray-500 hover:text-[#015FA6] transition-colors">
                                Privacy Policy
                            </a>
                            <a href="/contact" className="text-sm text-gray-500 hover:text-[#015FA6] transition-colors">
                                Contact Us
                            </a>
                        </div>
                    </div>
                </footer>
            </div>

            {/* Schedule Under Maintenance Modal */}
            {showScheduleModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowScheduleModal(false)}>
                    <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-end mb-2">
                            <button onClick={() => setShowScheduleModal(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                                <X className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Wrench className="w-8 h-8 text-orange-500" />
                            </div>
                            <h3 className="text-lg font-bold text-[#0B111E] mb-2">Under Maintenance</h3>
                            <p className="text-sm text-gray-500 mb-6">Scheduling interviews is currently under maintenance. We're working on bringing this feature to you soon.</p>
                            <button onClick={() => setShowScheduleModal(false)} className="w-full py-3 bg-[#015FA6] text-white font-semibold text-sm rounded-xl hover:bg-[#014a85] transition-colors">
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </CompanyLayout>
    );
};

export default Dashboard;
